import { TAuthTokenMessage } from "../data/AppInterface";
import { AUTH_TOKEN_MESSAGE_TYPE_ENUM } from "./AppEnumerations";
import { clearFileUrlCache } from "./SignedFileUrlCache";

type TSessionEndedListener = () => void;

const CHANNEL_NAME = "auth-token";
const REFRESH_LOCK_NAME = "auth-token-refresh";
const TOKEN_REQUEST_TIMEOUT_MS = 300;

let accessToken: string | null = null;
let isRestoringSession = false;
let channel: BroadcastChannel | null = null;
let resolveTokenRequest: ((token: string | null) => void) | null = null;
let localRefreshQueue: Promise<unknown> = Promise.resolve();
const sessionEndedListeners = new Set<TSessionEndedListener>();

const isBroadcastChannelExists =
  typeof window !== "undefined" && "BroadcastChannel" in window;

const handleTabMessage = (event: MessageEvent<TAuthTokenMessage>) => {
  const message = event.data;
  switch (message.type) {
    case AUTH_TOKEN_MESSAGE_TYPE_ENUM.TokenRequest:
      if (accessToken) {
        postTabMessage({
          type: AUTH_TOKEN_MESSAGE_TYPE_ENUM.Token,
          token: accessToken,
        });
      }
      break;
    case AUTH_TOKEN_MESSAGE_TYPE_ENUM.Token:
      if (resolveTokenRequest) {
        resolveTokenRequest(message.token);
      } else if (accessToken || isRestoringSession) {
        accessToken = message.token;
      }
      break;
    case AUTH_TOKEN_MESSAGE_TYPE_ENUM.SessionEnded:
      if (accessToken) {
        clearSessionToken();
        sessionEndedListeners.forEach((listener) => listener());
      }
      break;
  }
};

const ensureChannel = (): BroadcastChannel | null => {
  if (!isBroadcastChannelExists) return null;
  if (channel) return channel;
  channel = new BroadcastChannel(CHANNEL_NAME);
  channel.onmessage = handleTabMessage;
  return channel;
};

const postTabMessage = (message: TAuthTokenMessage) => {
  try {
    ensureChannel()?.postMessage(message);
  } catch {
    return;
  }
};

export const getSessionToken = () => accessToken;

export const setSessionToken = (token: string) => {
  accessToken = token;
  postTabMessage({ type: AUTH_TOKEN_MESSAGE_TYPE_ENUM.Token, token });
};

export const clearSessionToken = () => {
  accessToken = null;
  clearFileUrlCache();
};

export const broadcastSessionEnded = () => {
  postTabMessage({ type: AUTH_TOKEN_MESSAGE_TYPE_ENUM.SessionEnded });
};

export const listenSessionEnded = (listener: TSessionEndedListener) => {
  ensureChannel();
  sessionEndedListeners.add(listener);
  return () => {
    sessionEndedListeners.delete(listener);
  };
};

export const requestTokenFromOtherTabs = () =>
  new Promise<string | null>((resolve) => {
    if (!ensureChannel()) {
      resolve(null);
      return;
    }
    const finish = (token: string | null) => {
      clearTimeout(timer);
      resolveTokenRequest = null;
      if (token) accessToken = token;
      resolve(token);
    };
    const timer = setTimeout(() => finish(null), TOKEN_REQUEST_TIMEOUT_MS);
    resolveTokenRequest = finish;
    postTabMessage({ type: AUTH_TOKEN_MESSAGE_TYPE_ENUM.TokenRequest });
  });

export const whileRestoringSession = async <T>(
  task: () => Promise<T>
): Promise<T> => {
  isRestoringSession = true;
  try {
    return await task();
  } finally {
    isRestoringSession = false;
  }
};

export const runWithRefreshLock = <T>(task: () => Promise<T>): Promise<T> => {
  if (typeof navigator !== "undefined" && navigator.locks) {
    return navigator.locks
      .request(REFRESH_LOCK_NAME, task)
      .then((result) => result);
  }
  const run = localRefreshQueue.then(task, task);
  localRefreshQueue = run.catch(() => undefined);
  return run;
};
